import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["video", "playButton", "overlay"]

  connect() {
    // Track video view on page load
    this.trackView()

    // Listen for video end to show play button and overlay again
    this.videoTarget.addEventListener('ended', () => {
      this.showPlayButton()
      this.showOverlay()
    })

    // Handle fullscreen exit
    document.addEventListener('fullscreenchange', () => this.handleFullscreenChange())
    document.addEventListener('webkitfullscreenchange', () => this.handleFullscreenChange())
    document.addEventListener('mozfullscreenchange', () => this.handleFullscreenChange())
    document.addEventListener('MSFullscreenChange', () => this.handleFullscreenChange())

    // Ensure overlay is visible on load
    this.showOverlay()
  }

  togglePlay(event) {
    event.preventDefault()

    if (this.videoTarget.paused) {
      // Start playing and request fullscreen
      const playPromise = this.videoTarget.play()

      if (playPromise !== undefined) {
        playPromise.then(() => {
          this.hidePlayButton()
          this.hideOverlay()
          this.requestFullscreen()
        }).catch(error => {
          console.error('Error playing video:', error)
        })
      } else {
        this.hidePlayButton()
        this.hideOverlay()
        this.requestFullscreen()
      }
    } else {
      this.videoTarget.pause()
      this.showPlayButton()
      this.showOverlay()
    }
  }

  requestFullscreen() {
    // Request fullscreen on the video element
    const video = this.videoTarget

    if (video.requestFullscreen) {
      video.requestFullscreen().catch(err => {
        console.error('Error attempting to enable fullscreen:', err)
      })
    } else if (video.webkitRequestFullscreen) { // Safari
      video.webkitRequestFullscreen()
    } else if (video.mozRequestFullScreen) { // Firefox
      video.mozRequestFullScreen()
    } else if (video.msRequestFullscreen) { // IE/Edge
      video.msRequestFullscreen()
    } else {
      console.warn('Fullscreen API not supported')
    }
  }

  handleFullscreenChange() {
    // Check if we've exited fullscreen
    const isFullscreen = document.fullscreenElement ||
                        document.webkitFullscreenElement ||
                        document.mozFullScreenElement ||
                        document.msFullscreenElement

    if (!isFullscreen && !this.videoTarget.paused) {
      // User exited fullscreen, pause the video and show controls
      this.videoTarget.pause()
      this.showPlayButton()
      this.showOverlay()
    }
  }

  hidePlayButton() {
    if (this.hasPlayButtonTarget) {
      this.playButtonTarget.classList.add('hidden')
    }
  }

  showPlayButton() {
    if (this.hasPlayButtonTarget) {
      this.playButtonTarget.classList.remove('hidden')
    }
  }

  hideOverlay() {
    if (this.hasOverlayTarget) {
      this.overlayTarget.classList.add('hidden')
    }
  }

  showOverlay() {
    if (this.hasOverlayTarget) {
      this.overlayTarget.classList.remove('hidden')
    }
  }

  trackPlay() {
    // Analytics: video played
    this.sendEvent('professor_mode_video_played')
  }

  trackComplete() {
    // Analytics: video watched to end
    this.sendEvent('professor_mode_video_completed')
  }

  trackView() {
    // Analytics: page viewed
    this.sendEvent('professor_mode_page_viewed')
  }

  sendEvent(eventName) {
    const csrfToken = document.querySelector('[name="csrf-token"]')?.content

    if (!csrfToken) {
      console.warn('CSRF token not found, skipping analytics event')
      return
    }

    fetch('/events', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken
      },
      body: JSON.stringify({
        event: eventName
      })
    }).catch(error => {
      console.error('Error tracking event:', error)
    })
  }
}
